<template>
  <div class="recharge-packages" :class="{ embedded: embedded }">
    <div class="container">
      <h2 v-if="!embedded" class="page-title">充值中心</h2>
      <div class="packages-grid">
        <div
          v-for="pkg in packages"
          :key="pkg.id"
          class="package-card"
          :class="{ 'selected': selectedPackage === pkg.id }"
          @click="selectPackage(pkg.id)"
        >
          <div class="package-price">￥{{ pkg.price }}</div>
          <!-- 主数值只显示按 1:10 换算的基础余额，赠送单独一行标出，
               否则用户看到"328 → 3868"无法对应 1 元 = 10 余额的比例 -->
          <div class="package-coins">
            <span class="coin-value">{{ baseCoins(pkg) }}</span>
            <span class="coin-unit">余额</span>
          </div>
          <div class="package-bonus" v-if="Number(pkg.bonus_coins) > 0">
            额外赠送 {{ pkg.bonus_coins }}，共 {{ totalCoins(pkg) }}
          </div>
          <div class="package-bonus plain" v-else>1 元 = 10 余额</div>
          <div class="package-tag" v-if="pkg.is_recommend || pkg.tag">{{ pkg.tag || '推荐' }}</div>
        </div>
      </div>
      <div class="payment-section">
        <div class="payment-methods">
          <div class="method-title">支付方式</div>
          <div class="method-options">
            <div class="method-item active">
              <svg t="1750299770377" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4570" width="32" height="32"><path d="M1024.0512 701.0304V196.864A196.9664 196.9664 0 0 0 827.136 0H196.864A196.9664 196.9664 0 0 0 0 196.864v630.272A196.9152 196.9152 0 0 0 196.864 1024h630.272a197.12 197.12 0 0 0 193.8432-162.0992c-52.224-22.6304-278.528-120.32-396.4416-176.64-89.7024 108.6976-183.7056 173.9264-325.3248 173.9264s-236.1856-87.2448-224.8192-194.048c7.4752-70.0416 55.552-184.576 264.2944-164.9664 110.08 10.3424 160.4096 30.8736 250.1632 60.5184 23.1936-42.5984 42.496-89.4464 57.1392-139.264H248.064v-39.424h196.9152V311.1424H204.8V267.776h240.128V165.632s2.1504-15.9744 19.8144-15.9744h98.4576V267.776h256v43.4176h-256V381.952h208.8448a805.9904 805.9904 0 0 1-84.8384 212.6848c60.672 22.016 336.7936 106.3936 336.7936 106.3936zM283.5456 791.6032c-149.6576 0-173.312-94.464-165.376-133.9392 7.8336-39.3216 51.2-90.624 134.4-90.624 95.5904 0 181.248 24.4736 284.0576 74.5472-72.192 94.0032-160.9216 150.016-253.0816 150.016z" fill="#009FE8" p-id="4571"></path></svg>
              <span class="method-name">支付宝</span>
            </div>
          </div>
        </div>
        <button
          class="pay-button"
          :disabled="!selectedPackage"
          @click="handlePay"
        >
          立即充值
        </button>
      </div>
    </div>

    <!-- 支付二维码弹窗 -->
    <div v-if="payModal.show" class="payment-modal-overlay" @click="closePayModal">
      <div class="payment-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">请扫码支付</h3>
          <button class="close-btn" @click="closePayModal">×</button>
        </div>
        <div class="modal-content">
          <div class="amount-info">
            <div class="pay-amount">￥{{ payModal.amount }}</div>
            <div class="pay-desc">请使用支付宝扫码支付</div>
          </div>
          <div class="qr-wrap">
            <img class="qr-image" :src="payModal.qrUrl" alt="收款二维码">
          </div>
          <p class="tip-desc">支付成功后请截图联系客服确认，余额将人工到账。</p>
          <button class="pay-close-btn" @click="closePayModal">关闭</button>
        </div>
      </div>
    </div>

    <PageFooter v-if="!embedded" />
    <MinorTip :show="minorTip.show" :title="minorTip.title" :message="minorTip.message" @close="closeMinorTip" />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import PageFooter from './Footer.vue';
import MinorTip from './MinorTip.vue';
import { getRealnameStatus } from '../api/realname';
import { rechargePackages } from '../api/recharge';
import erweimaImg from '../assets/erweima.jpg';

const DEFAULT_PACKAGES = [
  { id: 1, price: 6, coins: 60, bonus_coins: 0, total_coins: 60 },
  { id: 2, price: 12, coins: 120, bonus_coins: 6, total_coins: 126 },
  { id: 3, price: 30, coins: 300, bonus_coins: 30, total_coins: 330, tag: '实惠' },
  { id: 4, price: 68, coins: 680, bonus_coins: 88, total_coins: 768, is_recommend: 1, tag: '推荐' },
  { id: 5, price: 128, coins: 1280, bonus_coins: 198, total_coins: 1478, tag: '超值' },
  { id: 6, price: 328, coins: 3280, bonus_coins: 588, total_coins: 3868, tag: '最划算' }
];

export default {
  name: 'RechargePackages',
  components: {
    PageFooter,
    MinorTip
  },
  props: {
    embedded: { type: Boolean, default: false }
  },
  setup(props) {
    void props.embedded;
    const selectedPackage = ref(null);
    const packages = ref(DEFAULT_PACKAGES);
    const payModal = ref({ show: false, amount: 0, qrUrl: '' });
    const minorTip = ref({ show: false, title: '温馨提示', message: '' });

    const selectPackage = (id) => {
      selectedPackage.value = id;
    };

    // 基础余额固定按 1 元 = 10 余额换算。
    // 优先用接口的 coins，但若缺失或与 price*10 不符则以 price*10 为准，
    // 保证展示的比例恒为 1:10（后台数据录错也不会显示成别的比例）。
    const baseCoins = (pkg) => {
      const price = Number(pkg.price) || 0;
      const expected = Math.round(price * 10);
      const coins = Number(pkg.coins);
      return (coins > 0 && coins === expected) ? coins : expected;
    };
    // 总额 = 基础 + 赠送，不直接信 total_coins（可能与基础/赠送不自洽）
    const totalCoins = (pkg) => baseCoins(pkg) + (Number(pkg.bonus_coins) || 0);

    // 拉取套餐（接口不可用时回退默认列表）
    const loadPackages = async () => {
      try {
        const res = await rechargePackages();
        if (res.code === 1 && res.data && res.data.list && res.data.list.length) {
          packages.value = res.data.list.map(p => ({
            id: p.id,
            price: p.price,
            coins: p.coins,
            bonus_coins: p.bonus_coins,
            total_coins: p.total_coins,
            is_recommend: p.is_recommend,
            tag: ''
          }));
        }
      } catch (e) {
        console.warn('套餐拉取失败，使用默认列表', e);
      }
    };

    const handlePay = async () => {
      const pkg = packages.value.find(p => p.id === selectedPackage.value);
      if (!pkg) return;

      // 未成年人保护前置校验（充值前拦截）
      try {
        const res = await getRealnameStatus();
        if (res.code === 1) {
          const s = res.data;
          const cfg = s.config || {};
          if (s.minor_mode) {
            minorTip.value = { show: true, title: '青少年模式', message: '您当前处于青少年模式，不可进行充值或打赏' };
            return;
          }
          if (s.is_minor) {
            if (cfg.in_curfew) {
              minorTip.value = { show: true, title: '宵禁时段', message: `宵禁时段（${cfg.night_start}:00-${cfg.night_end}:00）未成年人不可充值` };
              return;
            }
            if (cfg.force_mode && s.minor_mode) {
              minorTip.value = { show: true, title: '青少年模式', message: '您当前处于青少年模式，不可进行充值' };
              return;
            }
            if (+pkg.price > +cfg.single_limit) {
              minorTip.value = { show: true, title: '超出限额', message: `单笔充值不得超过 ￥${cfg.single_limit}` };
              return;
            }
          }
        }
      } catch (e) {
        console.error('实名状态获取失败', e);
      }

      startPay(pkg);
    };

    // 直接弹出收款二维码（真实支付未对接，扫码后人工确认到账）
    const startPay = (pkg) => {
      payModal.value = {
        show: true,
        amount: pkg.price,
        qrUrl: erweimaImg
      };
    };

    const closePayModal = () => {
      payModal.value.show = false;
    };

    const closeMinorTip = () => {
      minorTip.value.show = false;
    };

    onMounted(() => {
      loadPackages();
    });

    return {
      packages,
      selectedPackage,
      payModal,
      minorTip,
      selectPackage,
      baseCoins,
      totalCoins,
      handlePay,
      closePayModal,
      closeMinorTip
    };
  }
}
</script>

<style scoped>
.recharge-packages {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f6f7f9;
  padding: 40px 0;
}

.recharge-packages.embedded {
  min-height: auto;
  padding: 0;
  background: transparent;
}

.recharge-packages .container {
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.recharge-packages.embedded .container {
  max-width: none;
  margin: 0;
  padding: 0;
}

/* 内嵌到个人中心时，外层已是白底卡片，套餐卡改为描边区分 */
.recharge-packages.embedded .package-card {
  border: 1px solid #e8ecf1;
  background: #fcfdfe;
}

.recharge-packages.embedded .package-card.selected {
  border-color: #ff9600;
  background: #fff9f0;
}

.recharge-packages.embedded .payment-section {
  border: 1px solid #e8ecf1;
  background: #fcfdfe;
}

.recharge-packages.embedded .packages-grid {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  color: #1a1a1a;
  margin-bottom: 30px;
  font-weight: 600;
}

.packages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.package-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.package-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.package-card.selected {
  border-color: #ff9600;
  background: #fff9f0;
}

.package-price {
  font-size: 28px;
  color: #ff9600;
  font-weight: 600;
  margin-bottom: 12px;
}

.package-coins {
  margin-bottom: 8px;
}

.coin-value {
  font-size: 20px;
  color: #1a1a1a;
  font-weight: 500;
}

.coin-unit {
  font-size: 14px;
  color: #666;
  margin-left: 4px;
}

.package-bonus {
  color: #ff9600;
  font-size: 14px;
}

/* 无赠送时用弱化样式展示基础比例 */
.package-bonus.plain {
  color: #999;
  font-size: 12px;
}

.package-tag {
  position: absolute;
  top: 0;
  right: 20px;
  background: #ff9600;
  color: #fff;
  padding: 4px 12px;
  border-radius: 0 0 8px 8px;
  font-size: 12px;
}

.payment-section {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
}

.method-title {
  font-size: 16px;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.method-options {
  display: flex;
  gap: 20px;
}

.method-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.method-item:hover {
  border-color: #ff9600;
}

.method-item.active {
  border-color: #ff9600;
  background: #fff9f0;
}

.method-name {
  font-size: 14px;
  color: #1a1a1a;
}

.pay-button {
  width: 100%;
  max-width: 300px;
  height: 44px;
  background: #ff9600;
  color: #fff;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 20px;
}

.pay-button:hover {
  background: #ff7d00;
}

.pay-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 支付弹窗 */
.payment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.payment-modal {
  background: #fff;
  border-radius: 16px;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from { opacity: 0; transform: translateY(-50px) scale(0.9); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-content {
  padding: 24px;
  text-align: center;
}

.amount-info {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.pay-amount {
  font-size: 32px;
  font-weight: 600;
  color: #ff9600;
  margin-bottom: 8px;
}

.pay-desc {
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
}

.qr-wrap {
  margin: 20px auto;
  width: 240px;
  height: 240px;
  padding: 12px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.qr-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.tip-desc {
  font-size: 14px;
  color: #666;
  margin: 16px 0 0;
}

.pay-close-btn {
  display: block;
  width: 100%;
  max-width: 300px;
  margin: 20px auto 0;
  height: 44px;
  background: #ff9600;
  color: #fff;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
}

.pay-close-btn:hover {
  background: #ff7d00;
}
</style>
